import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DataTable,
  ExportCsvButton,
  FilterBar,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
} from "@/components/admin/admin-kit";
import {
  admDeletePushSubscription,
  admPushStats,
  admPushSubscribers,
  admSearchMembers,
  admSendPush,
} from "@/lib/push.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/notifications")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/notifications",
      "Notifications | Control room",
      "Browser notification management and broadcasts.",
    ),
  component: AdminNotifications,
});

type Member = { id: string; display_name: string | null; slug: string | null };

function AdminNotifications() {
  const statsFn = useServerFn(admPushStats);
  const subsFn = useServerFn(admPushSubscribers);
  const sendFn = useServerFn(admSendPush);
  const deleteFn = useServerFn(admDeletePushSubscription);
  const searchFn = useServerFn(admSearchMembers);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [target, setTarget] = useState<"all" | "logged_in" | "user">("all");
  const [memberQuery, setMemberQuery] = useState("");
  const [member, setMember] = useState<Member | null>(null);
  const [matches, setMatches] = useState<Member[]>([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [image, setImage] = useState("");
  const [alsoInApp, setAlsoInApp] = useState(true);
  const [requireInteraction, setRequireInteraction] = useState(false);

  const stats = useQuery({ queryKey: ["adm-push-stats"], queryFn: () => statsFn({ data: undefined }) });
  const subscribers = useQuery({
    queryKey: ["adm-push-subs", search],
    queryFn: () => subsFn({ data: { search } }),
  });

  const send = useMutation({
    mutationFn: () =>
      sendFn({
        data: {
          title: title.trim(),
          body: body.trim(),
          url: url.trim() || "/",
          image: image.trim() || undefined,
          alsoInApp,
          requireInteraction,
          target,
          targetUser: target === "user" ? (member?.id ?? undefined) : undefined,
        },
      }),
    onSuccess: (r) => {
      toast.success(`Sent to ${r.sent} of ${r.total} devices${r.failed ? ` · ${r.failed} failed` : ""}`);
      setTitle("");
      setBody("");
      void qc.invalidateQueries({ queryKey: ["adm-push-stats"] });
      void qc.invalidateQueries({ queryKey: ["adm-push-subs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Device removed");
      void qc.invalidateQueries({ queryKey: ["adm-push-subs"] });
      void qc.invalidateQueries({ queryKey: ["adm-push-stats"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function lookup(q: string) {
    setMemberQuery(q);
    if (q.trim().length < 2) {
      setMatches([]);
      return;
    }
    try {
      const rows = (await searchFn({ data: { q: q.trim() } })) as Member[];
      setMatches(rows);
    } catch {
      setMatches([]);
    }
  }

  const rows = subscribers.data ?? [];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Devices registered" value={stats.data?.totalSubscriptions ?? "—"} tone="hot" />
        <StatCard label="Active devices" value={stats.data?.activeSubscriptions ?? "—"} tone="ok" />
        <StatCard label="Members reachable" value={stats.data?.distinctUsers ?? "—"} />
        <StatCard label="Broadcasts sent" value={stats.data?.recent.length ?? "—"} />
      </div>

      <Section
        title="Send a notification"
        description="Delivered to browsers that opted in. Optionally mirrored into the in-app inbox."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="push-title">Title</Label>
            <Input
              id="push-title"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="A new board just went live"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="push-url">Link</Label>
            <Input
              id="push-url"
              value={url}
              maxLength={300}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/hot"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="push-body">Message</Label>
            <Textarea
              id="push-body"
              value={body}
              maxLength={400}
              rows={3}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Boost your listing today and climb the chart."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="push-image">Image URL (optional)</Label>
            <Input
              id="push-image"
              value={image}
              maxLength={600}
              onChange={(e) => setImage(e.target.value)}
              placeholder="https://behot.now/og/behot-now-hot.jpg"
            />
          </div>
          <div className="space-y-2">
            <Label>Audience</Label>
            <SelectFilter
              value={target}
              onChange={(v) => setTarget(v as typeof target)}
              options={[
                { value: "all", label: "Everyone who opted in" },
                { value: "logged_in", label: "Signed-in members only" },
                { value: "user", label: "One member" },
              ]}
            />
          </div>

          {target === "user" && (
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="push-member">Member</Label>
              <Input
                id="push-member"
                value={memberQuery}
                onChange={(e) => void lookup(e.target.value)}
                placeholder="Search by name or handle"
              />
              {member && (
                <p className="text-sm text-muted-foreground">
                  Selected: <strong>{member.display_name ?? member.slug ?? member.id}</strong>
                </p>
              )}
              {matches.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {matches.map((m) => (
                    <Button
                      key={m.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setMember(m);
                        setMatches([]);
                        setMemberQuery(m.display_name ?? m.slug ?? m.id);
                      }}
                    >
                      {m.display_name ?? m.slug ?? m.id}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6 md:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={alsoInApp} onCheckedChange={(v) => setAlsoInApp(Boolean(v))} />
              Also add to the in-app inbox
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={requireInteraction}
                onCheckedChange={(v) => setRequireInteraction(Boolean(v))}
              />
              Keep on screen until dismissed
            </label>
            <Button
              disabled={send.isPending || !title.trim() || !body.trim() || (target === "user" && !member)}
              onClick={() => send.mutate()}
            >
              {send.isPending ? "Sending…" : "Send notification"}
            </Button>
          </div>
        </div>
      </Section>

      <Section title="Registered devices" description="Every browser that opted in to notifications.">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Name, handle, platform…" />
          <ExportCsvButton rows={rows} filename="push-subscribers.csv" />
        </FilterBar>
        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "display_name",
              header: "Member",
              cell: (r) => r.display_name ?? r.slug ?? (r.user_id ? r.user_id.slice(0, 8) : "Anonymous"),
            },
            { key: "platform", header: "Platform", cell: (r) => r.platform || "—" },
            { key: "locale", header: "Locale", cell: (r) => r.locale || "—" },
            { key: "timezone", header: "Timezone", cell: (r) => r.timezone || "—" },
            {
              key: "disabled",
              header: "Status",
              cell: (r) =>
                r.disabled ? (
                  <Badge variant="outline">Disabled</Badge>
                ) : (
                  <Badge>Active</Badge>
                ),
            },
            {
              key: "last_seen_at",
              header: "Last seen",
              cell: (r) => new Date(r.last_seen_at as string).toLocaleString(),
            },
            {
              key: "actions",
              header: "",
              cell: (r) => (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(r.id)}>
                  Remove
                </Button>
              ),
            },
          ]}
        />
      </Section>

      <Section title="Broadcast history" description="The last 30 notifications sent from the control room.">
        <DataTable
          rows={stats.data?.recent ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: "created_at", header: "Sent", cell: (r) => new Date(r.created_at).toLocaleString() },
            { key: "title", header: "Title", cell: (r) => r.title },
            { key: "target", header: "Audience", cell: (r) => r.target },
            { key: "sent_count", header: "Delivered", cell: (r) => `${r.sent_count}/${r.total_count}` },
            { key: "failed_count", header: "Failed", cell: (r) => r.failed_count },
          ]}
        />
      </Section>
    </div>
  );
}
